import { useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import type {
  AuditFilters,
  AuditLogPage,
  AuditLogRow,
} from "@/lib/audit-log/audit-log-service";
import { PAGE_SIZE } from "@/lib/audit-log/audit-log-service";
import { ADMIN_TIME_ZONE, formatAdminDate } from "@/lib/format/admin-time";

interface Props {
  filters: AuditFilters;
  page: AuditLogPage;
}

function formatDateTime(d: Date | string): string {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // Pinned: the server render (UTC) and the browser must agree.
    timeZone: ADMIN_TIME_ZONE,
    timeZoneName: "short",
  });
}

function targetHref(row: AuditLogRow): string | null {
  if (!row.targetType || !row.targetId) return null;
  switch (row.targetType) {
    case "user":
      return `/admin/users/${row.targetId}`;
    case "group":
      return `/admin/groups/${row.targetId}`;
    case "notification":
      return `/admin/notifications`;
    default:
      return null;
  }
}

// Filter bounds are IST day edges; show the IST day they belong to.
function toDateInput(d: Date | null): string {
  return d ? formatAdminDate(d) : "";
}

export function AuditLogView({ filters, page }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const [actor, setActor] = useState(filters.actor ?? "");
  const [action, setAction] = useState(filters.action ?? "");
  const [from, setFrom] = useState(toDateInput(filters.from));
  const [to, setTo] = useState(toDateInput(filters.to));
  const [expanded, setExpanded] = useState<string | null>(null);

  const apply = (nextPage: number = 1) => {
    const p = new URLSearchParams(searchParams?.toString() ?? "");
    p.delete("page");
    p.delete("actor");
    p.delete("action");
    p.delete("from");
    p.delete("to");
    if (actor.trim()) p.set("actor", actor.trim());
    if (action.trim()) p.set("action", action.trim());
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    if (nextPage > 1) p.set("page", String(nextPage));
    navigate(`${pathname}?${p.toString()}`);
  };

  const clear = () => {
    setActor("");
    setAction("");
    setFrom("");
    setTo("");
    navigate(pathname);
  };

  const totalPages = Math.max(1, Math.ceil(page.total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 grid gap-2 md:grid-cols-[1fr_1fr_160px_160px_auto]">
        <input
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply(1)}
          placeholder="Actor email"
          className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 font-[family-name:var(--font-jetbrains)]"
        />
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply(1)}
          placeholder="Action (e.g. user.suspend)"
          className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 font-[family-name:var(--font-jetbrains)]"
        />
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12px] text-white focus:outline-none focus:border-emerald-500/30"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12px] text-white focus:outline-none focus:border-emerald-500/30"
        />
        <div className="flex gap-2">
          <button
            onClick={() => apply(1)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-black text-[12px] font-semibold hover:bg-emerald-400 transition-all"
          >
            <Search className="w-3.5 h-3.5" />
            Filter
          </button>
          <button
            onClick={clear}
            className="inline-flex items-center gap-1 px-2 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12px] text-zinc-400 hover:text-white transition-all"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/[0.06] text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3">When</th>
              <th className="px-5 py-3">Actor</th>
              <th className="px-5 py-3">Action</th>
              <th className="px-5 py-3">Target</th>
              <th className="px-5 py-3">Metadata</th>
            </tr>
          </thead>
          <tbody>
            {page.rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-5 py-8 text-center text-[12px] text-zinc-500"
                >
                  No entries match the current filters.
                </td>
              </tr>
            )}
            {page.rows.map((row) => {
              const href = targetHref(row);
              const isOpen = expanded === row.id;
              return (
                <tr
                  key={row.id}
                  className="border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] align-top"
                >
                  <td className="px-5 py-3 text-[12px] text-zinc-300 whitespace-nowrap font-[family-name:var(--font-jetbrains)]">
                    {formatDateTime(row.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-[12px] text-zinc-300 font-[family-name:var(--font-jetbrains)]">
                    {row.actorEmail}
                  </td>
                  <td className="px-5 py-3 text-[12px] text-emerald-300 font-[family-name:var(--font-jetbrains)]">
                    {row.action}
                  </td>
                  <td className="px-5 py-3 text-[12px]">
                    {row.targetType ? (
                      href ? (
                        <Link
                          to={href}
                          className="text-emerald-300 hover:text-emerald-200 font-[family-name:var(--font-jetbrains)]"
                        >
                          {row.targetType}/{row.targetId ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                          {row.targetType}/{row.targetId ?? "—"}
                        </span>
                      )
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[11px] text-zinc-400 max-w-[420px]">
                    {row.metadata ? (
                      <button
                        onClick={() =>
                          setExpanded(isOpen ? null : row.id)
                        }
                        className="text-left w-full"
                      >
                        {isOpen ? (
                          <pre className="whitespace-pre-wrap font-[family-name:var(--font-jetbrains)]">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        ) : (
                          <span className="text-zinc-500 hover:text-zinc-300 underline decoration-dotted underline-offset-2">
                            View
                          </span>
                        )}
                      </button>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-[12px] text-zinc-400">
        <div>
          Page {page.page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => apply(page.page - 1)}
            disabled={page.page <= 1}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] hover:text-white disabled:opacity-30 transition-all"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </button>
          <button
            onClick={() => apply(page.page + 1)}
            disabled={!page.hasMore}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] hover:text-white disabled:opacity-30 transition-all"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
